import React, { useState, useEffect, useRef, Fragment } from 'react';
import { ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import T from 'Components/Helpers/translate';
import MessageBlock from './MessageBlock';
import MultiActionCard from './MultiActionWriteCard';
import ContactScrapeCard from './ContactScrapeCard';
import FeedbackInline from './FeedbackInline';
import WriteCard from './WriteCard';
import WriteProposalGroupCard from './WriteProposalGroupCard';
import EmailDraftCard from './EmailDraftCard';
import OfferDraftCard from './OfferDraftCard';
import ToolProposalCard from './ToolProposalCard';
import ProspectingAddCard from './ProspectingAddCard';
import ChartCard from './ChartCard';
import ChatInput from './ChatInput';
import LoadingBar from './LoadingBar';
import DelegationChip, { extractDelegations, type DelegationData } from '../AIWorkspace/components/DelegationChip';
import ResearchedCompanies from './ResearchedCompanies';
import type {
	AssistantMessage,
	MultiActionConfig,
	ContactScrapeConfig,
	WriteProposal,
	EmailDraft,
	OfferDraft,
	ToolProposal,
	ProspectingAddConfig
} from './types';

type Props = {
	runTimestamp: string;
	messages: AssistantMessage[];
	defaultExpanded: boolean;
	conversationId: number | null;
	avatarUrl?: string | null;
	avatarColor?: string;
	assistantName?: string;
	globalMessageOffset: number;
	disabled?: boolean;
	streamingText?: string;
	loading?: boolean;
	toolStatus?: string;
	streaming?: boolean;
	onStop?: () => void;
	onMultiActionUpdate?: (id: string, updates: Partial<MultiActionConfig>) => void;
	onContactScrapeUpdate?: (id: string, updates: Partial<ContactScrapeConfig>) => void;
	onWriteProposalUpdate?: (index: number, update: Partial<WriteProposal>) => void;
	onWriteProposalGroupItemUpdate?: (messageIndex: number, itemIndex: number, update: Partial<WriteProposal>) => void;
	onApprovalsGroupEmailItemUpdate?: (messageIndex: number, itemIndex: number, update: Partial<EmailDraft>) => void;
	onEmailDraftUpdate?: (index: number, update: Partial<EmailDraft>) => void;
	onOfferDraftUpdate?: (index: number, update: Partial<OfferDraft>) => void;
	onToolProposalUpdate?: (index: number, update: Partial<ToolProposal>) => void;
	onProspectingAddUpdate?: (index: number, update: Partial<ProspectingAddConfig>) => void;
	onFeedback?: (
		messageIndex: number,
		type: 'up' | 'down',
		reason: string
	) => Promise<{ saved: boolean; savedValue?: string } | null> | void;
	onRegenerate?: (messageIndex: number) => void | Promise<void>;
	onSendFollowUp?: (text: string, runId: string, files?: File[]) => void;
	agents?: Array<{ id: number; name: string }>;
	onAgentClick?: (id: number) => void;
	onTaskClick?: (id: number) => void;
	onRoutineClick?: (id: number) => void;
	/** WEB-6800 — open a delegated child conversation (from a DelegationChip) in the drawer. */
	onOpenDelegation?: (delegation: DelegationData) => void;
	/** WEB-6797 — run header metadata. Decoupled scalars (not the AgentRun type)
	 *  so RunSection stays independent of the resource layer. All optional:
	 *  user-message groups with no backing run render the plain header. */
	runStatus?: string | null;
	/** Customer-facing credits (1 credit = 1 SEK), computed server-side. */
	runCostCredits?: number | null;
	runStartedAt?: string | null;
	runFinishedAt?: string | null;
	/** Pre-localized provenance label (e.g. "Scheduled", "Event hook", "Chat"),
	 *  derived from the conversation's contextType by the parent. */
	provenance?: string | null;
};

/** Two-decimal for small amounts, rounded thousands for large — mirrors the
 *  Usage view's formatter so credits read consistently across the workspace. */
const formatCredits = (credits?: number | null): string | null => {
	if (credits == null || !Number.isFinite(credits) || credits <= 0) return null;
	return credits >= 100 ? Math.round(credits).toLocaleString() : credits.toFixed(2);
};

const formatDuration = (start?: string | null, end?: string | null): string | null => {
	if (!start || !end) return null;
	const ms = new Date(end).getTime() - new Date(start).getTime();
	if (!Number.isFinite(ms) || ms < 0) return null;
	const totalSec = Math.round(ms / 1000);
	const sAbbr = T('assistant.runHeader.secondsAbbr') || 's';
	const mAbbr = T('assistant.runHeader.minutesAbbr') || 'm';
	const hAbbr = T('assistant.runHeader.hoursAbbr') || 'h';
	if (totalSec < 60) return `${totalSec}${sAbbr}`;
	const min = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	if (min < 60) return sec ? `${min}${mAbbr} ${sec}${sAbbr}` : `${min}${mAbbr}`;
	const hrs = Math.floor(min / 60);
	const remMin = min % 60;
	return remMin ? `${hrs}${hAbbr} ${remMin}${mAbbr}` : `${hrs}${hAbbr}`;
};

/** Terminal/in-flight status → label key + pill colour. Unknown statuses fall
 *  through to a neutral grey pill so a new backend status never breaks render. */
const STATUS_PILL: Record<string, { key: string; fallback: string; className: string }> = {
	succeeded: { key: 'assistant.runHeader.status.succeeded', fallback: 'Done', className: 'bg-green-1 text-green-6' },
	failed: { key: 'assistant.runHeader.status.failed', fallback: 'Failed', className: 'bg-red-1 text-red-6' },
	cancelled: {
		key: 'assistant.runHeader.status.cancelled',
		fallback: 'Cancelled',
		className: 'bg-grey-2 text-grey-9'
	},
	running: { key: 'assistant.runHeader.status.running', fallback: 'Running', className: 'bg-blue-1 text-blue-6' },
	pending: { key: 'assistant.runHeader.status.pending', fallback: 'Queued', className: 'bg-grey-2 text-grey-9' },
	awaiting_approval: {
		key: 'assistant.runHeader.status.awaitingApproval',
		fallback: 'Needs approval',
		className: 'bg-yellow-1 text-yellow-7'
	},
	awaiting_input: {
		key: 'assistant.runHeader.status.awaitingInput',
		fallback: 'Needs input',
		className: 'bg-yellow-1 text-yellow-7'
	}
};

const formatRunDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	const now = new Date();
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterdayStart = new Date(todayStart);
	yesterdayStart.setDate(yesterdayStart.getDate() - 1);
	const time = date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
	if (date >= todayStart) return `Today ${time}`;
	if (date >= yesterdayStart) return `Yesterday ${time}`;
	return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' }) + ' ' + time;
};

const RunSection = ({
	runTimestamp,
	messages,
	defaultExpanded,
	conversationId,
	avatarUrl,
	avatarColor,
	assistantName,
	globalMessageOffset,
	disabled,
	streamingText,
	loading,
	toolStatus,
	streaming,
	onStop,
	onMultiActionUpdate,
	onContactScrapeUpdate,
	onWriteProposalUpdate,
	onWriteProposalGroupItemUpdate,
	onApprovalsGroupEmailItemUpdate,
	onEmailDraftUpdate,
	onOfferDraftUpdate,
	onToolProposalUpdate,
	onProspectingAddUpdate,
	onFeedback,
	onRegenerate,
	onSendFollowUp,
	agents,
	onAgentClick,
	onTaskClick,
	onRoutineClick,
	onOpenDelegation,
	runStatus,
	runCostCredits,
	runStartedAt,
	runFinishedAt,
	provenance
}: Props) => {
	const [expanded, setExpanded] = useState(defaultExpanded);
	// Follow the "latest run" signal on transition: when a newer run arrives this
	// section stops being the latest (defaultExpanded true→false) and collapses,
	// so only the newest run stays open (WEB-7897). A manual toggle changes
	// `expanded` but not `defaultExpanded`, so it never triggers this and is
	// preserved.
	const prevDefaultExpanded = useRef(defaultExpanded);
	useEffect(() => {
		if (defaultExpanded !== prevDefaultExpanded.current) {
			prevDefaultExpanded.current = defaultExpanded;
			setExpanded(defaultExpanded);
		}
	}, [defaultExpanded]);
	const credits = formatCredits(runCostCredits);
	const duration = formatDuration(runStartedAt, runFinishedAt);
	const pill = runStatus ? STATUS_PILL[runStatus] : undefined;
	const assistantCount = messages.filter(m => m.role === 'assistant').length;
	const endRef = useRef<HTMLDivElement>(null);

	// Streaming autoscroll for run-based conversations lives in WorkspaceChat —
	// it watches the shared scroller's pinned-to-bottom state so we don't yank
	// the user back down if they've scrolled up to read something.

	// On initial mount of an already-expanded run, jump to its latest message.
	// Scheduled-run conversations open with the last run expanded but the
	// main scroller parks at the top — this anchors the view on the newest
	// content so the user doesn't have to scroll down to find it.
	useEffect(() => {
		if (defaultExpanded) {
			endRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
		}
		// Only on mount — subsequent expansions are user-initiated and we
		// don't want to yank their scroll position.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="border-t-2 border-border pt-6 mt-4 first:border-t-0 first:pt-0 first:mt-0">
			<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				className="flex items-center gap-2.5 py-3 rounded-lg hover:bg-grey-1 transition-colors cursor-pointer text-left sticky top-0 z-20 bg-background border-b border-transparent [&:not(:first-child)]:border-border/20"
				style={{
					background: 'var(--pr-surface)',
					// Extend past the parent's horizontal padding so the follow-up
					// input's focus glow can't bleed out at the sides of the
					// sticky header as the input scrolls behind it. Width =
					// 100% of parent + 48px total; shift left by 24 to stay
					// centred.
					width: 'calc(100% + 48px)',
					marginLeft: -24,
					paddingLeft: 28,
					paddingRight: 28
				}}
			>
				{expanded ? (
					<ChevronDown className="h-4 w-4 text-grey-8 shrink-0" />
				) : (
					<ChevronRight className="h-4 w-4 text-grey-8 shrink-0" />
				)}
				<span className="pr-row-icon text-brand">
					<Clock />
				</span>
				<span className="text-body-sm text-grey-10 font-normal">
					{runTimestamp ? formatRunDate(runTimestamp) : 'Run'}
				</span>
				{provenance && (
					<span className="text-body-sm text-grey-7 font-normal truncate max-w-[40%]">
						{`· ${provenance}`}
					</span>
				)}
				<span className="ml-auto flex items-center gap-3 text-body-sm text-grey-7 shrink-0">
					{credits != null && (
						<span>{`${credits} ${T('assistant.runHeader.creditsUnit') || 'credits'}`}</span>
					)}
					{duration && <span>{duration}</span>}
					{pill && (
						<span className={cn('px-1.5 py-0.5 rounded text-xs font-medium', pill.className)}>
							{T(pill.key) || pill.fallback}
						</span>
					)}
					{!expanded && !pill && <span>{`${assistantCount} message${assistantCount !== 1 ? 's' : ''}`}</span>}
				</span>
			</button>
			{expanded &&
				(() => {
					// Cards (proposals / multi-action / email drafts) often stream in
					// before the assistant's closing text, so hoist them below the
					// streaming/loading indicators — but ONLY for the current (last)
					// turn. A completed run's cards must stay in place once follow-up
					// turns are added below it; hoisting every card would push the
					// original run's proposals beneath a later follow-up. Mirrors
					// MessageThread's trailingCards logic.
					const isCard = (m: AssistantMessage) =>
						!!m.writeProposalGroup ||
						!!m.writeProposal ||
						!!m.multiAction ||
						!!m.emailDraft ||
						!!m.offerDraft ||
						!!m.toolProposal ||
						!!m.prospectingAdd;
					const indexed = messages.map((m, i) => ({ m, i }));
					let lastUserIdx = -1;
					for (let i = messages.length - 1; i >= 0; i--) {
						if (messages[i].role === 'user') {
							lastUserIdx = i;
							break;
						}
					}
					const lastIsCard = messages.length > 0 && isCard(messages[messages.length - 1]);
					const trailingStart =
						lastIsCard && lastUserIdx >= 0
							? messages.findIndex((m, i) => i > lastUserIdx && isCard(m))
							: -1;
					const trailingIdx = new Set(
						trailingStart === -1
							? []
							: indexed
									.slice(trailingStart)
									.filter(({ m }) => isCard(m))
									.map(({ i }) => i)
					);
					const inline = indexed.filter(({ i }) => !trailingIdx.has(i));
					const trailing = indexed.filter(({ i }) => trailingIdx.has(i));
					const renderMessage = ({ m: msg, i }: { m: AssistantMessage; i: number }) => {
						const globalIndex = globalMessageOffset + i;
						if ((msg.metadata as { hidden?: boolean } | undefined)?.hidden) return null;
						return (
							<Fragment key={msg.id ?? globalIndex}>
								{msg.chart ? (
									<ChartCard spec={msg.chart} />
								) : (
									<>
										<MessageBlock
											role={msg.role}
											content={msg.content}
											images={msg.images}
											// WEB-11181: without this a generated document renders no
											// chip inside a run — the file is stored, filed on the
											// conversation and present in `metadata.attachments`, but
											// invisible to the user, and a reload doesn't help because
											// a routine-pinned conversation always renders run-grouped.
											attachments={msg.attachments}
											regDate={msg.regDate}
											avatarUrl={msg.role === 'assistant' ? avatarUrl : undefined}
											avatarColor={msg.role === 'assistant' ? avatarColor : undefined}
											assistantName={msg.role === 'assistant' ? assistantName : undefined}
											agents={agents}
											onAgentClick={onAgentClick}
											onTaskClick={onTaskClick}
											onRoutineClick={onRoutineClick}
										/>
										{msg.multiAction && (
											<MultiActionCard config={msg.multiAction} onUpdate={onMultiActionUpdate} />
										)}
										{msg.contactScrape && (
											<ContactScrapeCard
												config={msg.contactScrape}
												onUpdate={onContactScrapeUpdate}
											/>
										)}
										{msg.emailDraft && (
											<EmailDraftCard
												draft={msg.emailDraft}
												onUpdate={update => onEmailDraftUpdate?.(globalIndex, update)}
											/>
										)}
										{msg.offerDraft && (
											<OfferDraftCard
												draft={msg.offerDraft}
												onUpdate={update => onOfferDraftUpdate?.(globalIndex, update)}
											/>
										)}
										{msg.toolProposal && (
											<ToolProposalCard
												proposal={msg.toolProposal}
												onUpdate={update => onToolProposalUpdate?.(globalIndex, update)}
											/>
										)}
										{msg.prospectingAdd && (
											<ProspectingAddCard
												config={msg.prospectingAdd}
												onUpdate={(_id, update) =>
													onProspectingAddUpdate?.(globalIndex, update)
												}
												defaultExpanded
											/>
										)}
										{msg.writeProposalGroup && (
											<WriteProposalGroupCard
												items={msg.writeProposalGroup.items}
												onWriteItemUpdate={(itemIdx, update) =>
													onWriteProposalGroupItemUpdate?.(globalIndex, itemIdx, update)
												}
												onEmailItemUpdate={(itemIdx, update) =>
													onApprovalsGroupEmailItemUpdate?.(globalIndex, itemIdx, update)
												}
											/>
										)}
										{msg.writeProposal && (
											<WriteCard
												proposal={msg.writeProposal}
												onUpdate={update => onWriteProposalUpdate?.(globalIndex, update)}
											/>
										)}
										{/* WEB-7850: prominent, click-to-open banner below the reply so
										    the background task lands in the right place in the timeline
										    and is unmistakable — not a small right-aligned pill. */}
										{extractDelegations(msg.metadata).map((d, di) => (
											<DelegationChip
												key={`del-${globalIndex}-${di}`}
												delegation={d}
												onOpen={d => onOpenDelegation?.(d)}
											/>
										))}
										{/* WEB-14734: as in MessageThread. */}
										{msg.role === 'assistant' ? (
											<ResearchedCompanies metadata={msg.metadata} />
										) : null}
										{msg.role === 'assistant' && (
											<FeedbackInline
												messageIndex={globalIndex}
												conversationId={conversationId}
												onSubmit={(idx, type, reason) => onFeedback?.(idx, type, reason)}
												content={msg.content}
												onRegenerate={onRegenerate}
												disabled={disabled}
											/>
										)}
									</>
								)}
							</Fragment>
						);
					};
					return (
						<div className={cn('flex flex-col gap-5 mt-3')}>
							{inline.map(item => (
								<Fragment key={`row-${item.i}`}>{renderMessage(item)}</Fragment>
							))}
							{loading && !streamingText && (
								<div className="-mt-2.5">
									<LoadingBar
										status={toolStatus}
										avatarUrl={avatarUrl}
										avatarColor={avatarColor}
										assistantName={assistantName}
										inline
									/>
								</div>
							)}
							{streamingText && (
								<div className="-mt-2.5">
									<MessageBlock
										role="assistant"
										content={streamingText}
										streaming
										avatarUrl={avatarUrl}
										avatarColor={avatarColor}
										assistantName={assistantName}
										agents={agents}
										onAgentClick={onAgentClick}
										onTaskClick={onTaskClick}
										onRoutineClick={onRoutineClick}
									/>
									{loading && toolStatus && (
										<div className="flex items-center gap-2 pl-14 pt-1">
											<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:0ms]" />
											<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:150ms]" />
											<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:300ms]" />
											<span className="text-body-sm text-muted-foreground ml-1">
												{toolStatus}
											</span>
										</div>
									)}
								</div>
							)}
							{trailing.map(renderMessage)}
							<div ref={endRef} />
							{onSendFollowUp && runTimestamp && (
								<div
									className="sticky bottom-0 z-10 -mx-1 px-1 pt-3 pb-6"
									style={{
										background:
											'linear-gradient(to bottom, transparent 0, var(--pr-surface) 20px, var(--pr-surface) 100%)'
									}}
								>
									<ChatInput
										autoFocus={false}
										placeholder={'Follow up on this run…'}
										disabled={disabled}
										streaming={streaming}
										onStop={onStop}
										onSend={(text, files) => onSendFollowUp(text, runTimestamp, files)}
									/>
								</div>
							)}
						</div>
					);
				})()}
		</div>
	);
};

export default RunSection;
